'use client';

import * as React from 'react';

/**
 * Whether React has hydrated this component yet.
 *
 * ## Why a form needs to know
 *
 * A form whose only submit path is JavaScript — `onSubmit={handleSubmit(…)}`,
 * a server action called from the handler — is still a plain `<form>` in the
 * HTML the server sent. Until hydration attaches the handler, pressing its
 * button submits it the way the browser always has: with no `method`, that is a
 * **GET to the current URL with every named field in the query string**.
 *
 * On a sign-in form that puts the password in the address bar, and from there
 * into browser history, the `Referer` of anything the page loads next, and the
 * access log of every proxy in between. It is also why such a form appears to
 * "sometimes not submit": the entry is lost to a reload rather than sent.
 *
 * Gate the submit button on this, and neither can happen — the button is inert
 * for exactly as long as pressing it would do the wrong thing.
 *
 * `useSyncExternalStore` rather than an effect because it is the one form React
 * guarantees differs between the server render and the client: `false` while
 * rendering the HTML, `true` from the first client render onward, with no
 * intermediate paint that a user could click through.
 */
const subscribe = () => () => {};
const onClient = () => true;
const onServer = () => false;

export function useHydrated(): boolean {
  return React.useSyncExternalStore(subscribe, onClient, onServer);
}
